import { type ReactNode, type TextareaHTMLAttributes, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { normalizeErrors } from '../../error/utils';
import { ErrorField, type ErrorFieldProps } from './error-field';

export type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  size?: 'xs' | 'sm' | 'md' | 'lg';
  error?: ErrorFieldProps['error'];
  description?: string | ReactNode;
  label?: string | ReactNode;
  containerClassName?: string;
  showCounter?: boolean;
  counterLimit?: number;
  isNew?: boolean;
  extraElement?: ReactNode;
};

export function TextArea(props: TextAreaProps) {
  const {
    size = 'sm',
    error,
    description,
    label = '',
    containerClassName = '',
    showCounter = false,
    value = '',
    counterLimit = 1000,
    isNew = false,
    extraElement,
    ...textAreaProps
  } = props;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function handleDivClick() {
    textareaRef.current?.focus();
  }
  const baseClassNames =
    'bg-white shadow-1 rounded-md border border-branding-gray placeholder:text-gray text-black min-w-80 min-h-45 w-full outline-none focus-within:border-[1.5px] focus-within:border-branding-accent focus-within:shadow-accent';
  const labelClassNames = {
    xs: 'font-body-3-r text-black',
    sm: 'font-body-3-r text-black',
    md: 'font-body-2-r text-black',
    lg: 'font-body-2-r text-black',
  }[size];
  const sizeClassNames = {
    xs: 'font-body-2-m py-1.5 px-2.5',
    sm: 'font-body-2-m py-2 px-3.5',
    md: 'font-body-2-m py-2.5 px-4',
    lg: 'font-body-1-m py-2.5 px-4.5',
  }[size];
  const labelDescriptionSizes = {
    xs: 'font-body-3-r',
    sm: 'font-body-3-r',
    md: 'font-body-2-r',
    lg: 'font-body-2-r',
  }[size];
  const errorClassNames = 'border-red-500 focus-within:shadow-error focus-within:border-red-500';
  const errors = normalizeErrors(error);
  const classNames = twMerge(
    baseClassNames,
    sizeClassNames,
    props.className ?? '',
    errors.length > 0 ? errorClassNames : '',
    isNew ? 'px-2' : ''
  );
  const textAreaClassNames = twMerge(
    'w-full outline-none',
    isNew ? 'bg-branding-accent-50 px-2' : ''
  );
  const descriptionRender =
    description != null ? (
      <p className={twMerge('text-branding-gray', labelDescriptionSizes)}>{description}</p>
    ) : null;

  const stringValue = String(value);

  const counterRender =
    showCounter && counterLimit > 0 ? (
      <p
        className={twMerge(
          'text-right text-branding-gray',
          labelDescriptionSizes,
          stringValue.length > counterLimit ? 'text-red-500' : 'text-branding-gray'
        )}
      >
        {stringValue.length} / {counterLimit}
      </p>
    ) : null;

  return (
    <div className={twMerge('grid w-full grid-flow-row gap-1.5', containerClassName)}>
      {label == null ? (
        <div className={classNames} onClick={handleDivClick}>
          <textarea
            {...textAreaProps}
            ref={textareaRef}
            className={textAreaClassNames}
            value={value}
          />
        </div>
      ) : (
        <label className={twMerge('grid grid-flow-row gap-1.5', labelDescriptionSizes)}>
          {typeof label === 'string' ? <p className={labelClassNames}>{label}</p> : label}
          <div className={classNames} onClick={handleDivClick}>
            <textarea
              {...textAreaProps}
              ref={textareaRef}
              className={textAreaClassNames}
              value={value}
            />
            {extraElement}
          </div>
        </label>
      )}
      {errors.length > 0 ? <ErrorField error={error} sizeField={size} /> : descriptionRender}
      {counterRender}
    </div>
  );
}
